import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import { offerTypeService, UpdateOfferTypeDto, OfferType } from "../../apis/offerTypes";

interface EditOfferTypeModalProps {
  open: boolean;
  onClose: () => void;
  offerType: OfferType | null;
}

const EditOfferTypeModal: React.FC<EditOfferTypeModalProps> = ({ open, onClose, offerType }) => {
  const [formData, setFormData] = useState<UpdateOfferTypeDto>({
    name: "",
    partner_percentage: 0,
    description: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    if (offerType) {
      setFormData({
        name: offerType.name,
        partner_percentage: offerType.partner_percentage,
        description: offerType.description || "",
        is_active: offerType.is_active,
      });
    }
  }, [offerType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const numValue = name.includes("percentage") ? parseFloat(value) || 0 : value;
    const boolValue = type === "checkbox" ? checked : undefined;
    
    setFormData((prev) => ({
      ...prev,
      [name]: boolValue !== undefined ? boolValue : numValue,
    }));
  };

  const handleSubmit = async () => {
    if (!offerType) return;

    if (!formData.name || (formData.partner_percentage !== undefined && (formData.partner_percentage <= 0 || formData.partner_percentage > 100))) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields. Partner percentage must be between 0 and 100.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await offerTypeService.updateOfferType(offerType._id, formData);
      setSnackbar({
        open: true,
        message: "Offer type updated successfully",
        severity: "success",
      });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update offer type",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Offer Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Partner Percentage"
              name="partner_percentage"
              type="number"
              value={formData.partner_percentage}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText="Percentage of teracar commission that goes to partner (0-100)"
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "Updating..." : "Update Offer Type"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default EditOfferTypeModal;

